<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {getCorp, listCorps, setCorp} from '@/services/api/corporation.js';
import { handleError } from '@/utils/errors';
import { useI18n } from 'vue-i18n';
import CardSelector from "@/components/common/CardSelector.vue";
import Pagination from "@/components/common/Pagination.vue";
import LanguageSwitcherHorizontal from "@/components/common/LanguageSwitcherHorizontal.vue";
import Text from "@/components/common/Text.vue";
import ButtonBack from "@/components/common/button/ButtonBack.vue";
import {favoritesStore} from "@/stores/favoritesStore.js";
import {getUser} from "@/services/api/auth.js";
import { useAuthStore } from "@/stores/authStore";
import { getSettingsRoute } from "@/utils/settingsRoutes.js";

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const corps = ref([]);
const currentPage = ref(1);
const itemsPerPage = 3;
const loading = ref(false);
const errorMessage = ref("");
const paginatedCorps = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage;
    return corps.value.slice(start, start + itemsPerPage);
});
const showPagination = computed(() => corps.value.length >= itemsPerPage);
const favStore = favoritesStore();
const currentCorp = ref(null);
const backRoute = computed(() => {
    return currentCorp.value?.id || authStore.isAdmin
        ? getSettingsRoute('/settings', route)
        : '/logout';
});
const redirectAfterCorpSelection = computed(() => {
    return authStore.signInRoute === '/rhdigital' ? '/rhdigital/mural' : '/home';
});
const fetchCorps = async () => {
    try {
        loading.value = true;
        const rawCorps = await listCorps({});

        corps.value = rawCorps.map(c => ({
            value: c.id,
            name: c.companyName
        }));
    } catch (error) {
        errorMessage.value = await handleError(error, t);
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const handleUpdateCorp = async ({ id }) => {
    try {
        loading.value = true;
        errorMessage.value = "";
        await setCorp({ id, advancedCredentials: (id === "*") ? true : null});
        if (id !== "*") {
            await favStore.loadFavorites();
        }
        router.push(redirectAfterCorpSelection.value);
    } catch (error) {
        errorMessage.value = await handleError(error, t);
        console.error(error);
    } finally {
        loading.value = false;
    }
};

onMounted(async () => {
    currentCorp.value = await getCorp();
    await fetchCorps();
});
</script>
<template>
    <div class="corporation-selection d-flex bg-white">
        <div class="trezo-form mx-auto d-flex">
            <div class="row align-items-center">
                <div class="w-640">
                    <div class="form-content">
                        <div class="text-center">
                            <v-img
                                class="logo mx-auto d-block mb-5"
                                src="/images/static/logos/logo-icon.png"
                                alt="logo"
                            />
                        </div>

                        <!--
                            O erro fica como aviso ACIMA da lista, nao no lugar
                            dela: escolher uma empresa sem acesso devolve um
                            erro, e esconder o seletor e o botao de voltar
                            junto deixava quem tentou presa nesta tela, sem
                            como escolher outra empresa nem sair.
                        -->
                        <div v-if="errorMessage" class="alert alert-danger mb-4">
                            {{ errorMessage }}
                        </div>
                        <div>
                            <div class="h-430">
                                <div class="text-center my-5">
                                    <Text
                                        tag="h4"
                                        weight="medium"
                                    >
                                        {{ $t('components.authentication.signIn.selectCorporation') }}
                                    </Text>
                                </div>

                                <div class="text-center mb-8">
                                    <Text
                                        tag="h5"
                                        weight="medium"
                                        size="sub-title"
                                    >
                                        {{ $t('components.authentication.signIn.availableCorporations') }}
                                    </Text>
                                </div>
                                <CardSelector
                                    :items="paginatedCorps.filter(item => item.value !== '*')"
                                    :loading="loading"
                                    @item-selected="item => handleUpdateCorp({ id: item })"
                                />

                                <v-row v-if="corps.some(item => item.value === '*')" class="d-flex justify-content-center my-4">
                                    <v-btn color="bg-primary" @click="handleUpdateCorp({ id: '*' })">
                                        <v-icon class="me-2">mdi-account</v-icon>
                                        {{ $t('components.authentication.signIn.loginAsAdmin')}}
                                    </v-btn>
                                </v-row>

                                <Pagination
                                    v-if="showPagination"
                                    :isPrevDisabled="currentPage === 1"
                                    :isNextDisabled="currentPage * itemsPerPage >= corps.length"
                                    :onPrev="() => currentPage--"
                                    :onNext="() => currentPage++"
                                />

                            </div>
                            <div>
                                <ButtonBack :route="backRoute" />
                            </div>
                        </div>
                        <LanguageSwitcherHorizontal />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.logo {
    width: 150px !important;
    display: block;
}

.corporation-selection {
    background-color: var(--whiteColor);
    height: 100vh;
    &.dark-theme {
        background-color: #0a0e19;
    }
    .trezo-form {
        max-width: 1255px;
        padding: {
            left: 12.5px;
            right: 12.5px;
        }
        .form-image {
            border-radius: 24px;
            margin-right: -25px;

            .v-img {
                border-radius: 24px;
                margin-bottom: -3px;
            }
        }
        .form-content {
            padding: 0 90px;

            .title {
                margin: {
                    top: 23px;
                    bottom: 23px;
                }
                h1 {
                    font-size: 28px;
                    margin-bottom: 7px;
                }
                p {
                    font-size: 16px;
                    color: #445164;
                }
            }
            .with-socials {
                margin-bottom: 23px;

                div {
                    flex-grow: 1;
                    margin: {
                        left: 6px;
                        right: 6px;
                    }
                    &:first-child {
                        margin-left: 0;
                    }
                    &:last-child {
                        margin-right: 0;
                    }
                }
                button {
                    width: 100%;
                    height: auto;
                    display: block;
                    min-width: auto;
                    padding: 10px 25px;
                    border-radius: 8px;
                    color: var(--blackColor);
                    border: 1px solid #d6dae1;
                    background: var(--whiteColor);
                    box-shadow: 0px 4px 16px -2px rgba(27, 37, 51, 0.06);

                    .v-responsive {
                        justify-content: center !important;
                    }
                }
            }
            form {
                .trezo-form-group {
                    .v-label {
                        margin-bottom: 12px;
                    }
                }
                .forgot-password {
                    a {
                        &:hover {
                            text-decoration: underline;
                        }
                    }
                }
                button {
                    width: 100%;
                    height: auto;
                    min-width: auto;
                    margin-top: 24px;
                    padding: 12px 25px;
                    border-radius: 7px;
                    color: var(--blackColor);
                    background-color: var(--primaryColor);
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    & {
                        font: {
                            size: 16px;
                            weight: 500;
                        }
                    }
                    i {
                        margin-right: 5px;
                    }
                }
                .info {
                    margin-top: 20px;

                    a {
                        &:hover {
                            text-decoration: underline;
                        }
                    }
                }
            }
        }
    }
}

/* Max width 767px */
@media only screen and (max-width: 1400px) {
    .corporation-selection {
        height: auto;
        padding: {
            top: 60px;
            bottom: 60px;
        }
        .trezo-form {
            max-width: 100%;

            .form-image {
                border-radius: 15px;
                margin: {
                    right: 0;
                    top: 25px;
                }
                img {
                    border-radius: 15px;
                }
            }
            .form-content {
                padding: 0 10px;

                .title {
                    margin: {
                        top: 17px;
                        bottom: 15px;
                    }
                    h1 {
                        font-size: 22px;
                    }
                    p {
                        font-size: 13px;
                    }
                }
                .with-socials {
                    margin-bottom: 20px;

                    div {
                        margin: {
                            left: 5px;
                            right: 5px;
                        }
                    }
                    button {
                        padding: 8px 15px;
                    }
                }
                form {
                    .trezo-form-group {
                        .v-label {
                            margin-bottom: 10px;
                        }
                    }
                    button {
                        padding: 12px 25px;
                        margin-top: 17px;
                        font-size: 13px;
                        i {
                            font-size: 20px;
                        }
                    }
                    .info {
                        margin-top: 15px;
                    }
                }
            }
        }
    }
}

/* Min width 576px to Max width 767px */
@media only screen and (min-width: 576px) and (max-width: 767px) {
    .corporation-selection {
        .trezo-form {
            max-width: 540px;

            .form-content {
                .title {
                    margin: {
                        top: 20px;
                        bottom: 17px;
                    }
                }
                form {
                    button {
                        margin-top: 20px;
                    }
                }
            }
        }
    }
}

/* Min width 768px to Max width 991px */
@media only screen and (min-width: 768px) and (max-width: 991px) {
    .corporation-selection {
        padding: {
            top: 80px;
            bottom: 80px;
        }
        .trezo-form {
            max-width: 720px;

            .form-image {
                margin: {
                    right: 0;
                    top: 25px;
                }
            }
            .form-content {
                padding-left: 0;

                .title {
                    margin: {
                        top: 20px;
                        bottom: 20px;
                    }
                    h1 {
                        font-size: 24px;
                    }
                    p {
                        font-size: 14px;
                    }
                }
                .with-socials {
                    margin-bottom: 20px;

                    div {
                        margin: {
                            left: 5px;
                            right: 5px;
                        }
                    }
                }
                form {
                    button {
                        font-size: 14px;
                        margin-top: 20px;
                        padding: 12px 25px;
                        i {
                            font-size: 22px;
                        }
                    }
                    .info {
                        margin-top: 15px;
                    }
                }
            }
        }
    }
}

/* Min width 992px to Max width 1199px */
@media only screen and (min-width: 992px) and (max-width: 1199px) {
    .corporation-selection {
        padding: {
            top: 100px;
            bottom: 100px;
        }
        .trezo-form {
            max-width: 960px;

            .form-image {
                margin-right: 0;
            }
            .form-content {
                padding: 0 15px;

                .title {
                    h1 {
                        font-size: 26px;
                    }
                    p {
                        font-size: 15px;
                    }
                }
                form {
                    button {
                        margin-top: 20px;
                        font-size: 15px;
                    }
                    .info {
                        margin-top: 15px;
                    }
                }
            }
        }
    }
}

/* Min width 1200px to Max width 1399px */
@media only screen and (min-width: 1200px) and (max-width: 1399px) {
    .corporation-selection {
        padding: {
            top: 120px;
            bottom: 120px;
        }
        .trezo-form {
            max-width: 1140px;

            .form-image {
                margin-right: 0;
            }
            .form-content {
                padding:0 25px;
            }
        }
    }
}

/* Min width 1600px */
@media only screen and (min-width: 1600px) {
    .corporation-selection {
        .trezo-form {
            .form-image {
                margin-right: -45px;
            }
            .form-content {
                padding: 0 50px;
            }
        }
    }
}

@media only screen and (max-width: 1000px) {
    .corporation-selection, .trezo-form {
        display: block!important;
    }
    .logo {
        width:  90px!important;
    }
}
</style>
